/**
 * Dependencies and licences › every package, not only the problems (2026-10-31, the product owner: "I want to see all
 * dependencies with licence and if something is not ok, not just ok"). One row per package of the SBOM (GET
 * /quality/sbom, CycloneDX: name, version, licence, dev or not, the services, the lockfile), joined with what the checks
 * found by package and version: a licence not allowed or unknown (this check's findings and licence counts), known
 * vulnerabilities (the Vulnerabilities check, its highest severity, a link to it), outdated (registry lookups on).
 * Problems sort first and are toned; All / Problems only, a licence filter (the licence chips set it too) and search.
 *
 * `LockfilesByService`: which lockfile each service has, or that it has none (with Create lockfile), so "lockfiles"
 * never reads as one count for several services.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getCheck, getSbom } from '../../services/QualityService';
import { Banner, Pill } from './QualityCommon';
import DataTable from '../table/DataTable';
import { SEVERITIES, errorText, passingMessage, plural, severityOf } from './qualityModel';

/** How many rows show at first, and how many more each "Show more" adds. */
export const PAGE = 200;

const keyOf = (name, version) => `${name}@${version}`;
const propOf = (component, name) => (component.properties || []).find((p) => p.name === name)?.value ?? null;

/** A CycloneDX component's licence as the check names it (an SPDX id, a name or an expression); null when none. */
function licenceOf(component) {
  const first = (component.licenses || [])[0];
  return first?.license?.id || first?.license?.name || first?.expression || null;
}

/** Problems first: the order the Status column sorts on (an OK package last). */
const RANK = { licence: 0, vulnerable: 1, unknown: 2, outdated: 3 };
const OK_RANK = 9;

/**
 * The table's rows: `{ key, name, version, licence, licenceState: 'ok'|'not_allowed'|'unknown', services, dev, direct
 * (true, false or null when the SBOM does not say), problems: [{ kind, label, tone, title? }], rank }`.
 * `licences`: the check's counts by licence (`allowed`); `findings`: this check's findings (licence-not-allowed,
 * licence-unknown); `vulnerabilities`: the Vulnerabilities check's findings (null: not run); `outdated`: the check's
 * outdated packages (null: registry lookups off).
 */
export function packageRows({ components = [], licences = [], findings = [], vulnerabilities = null, outdated = null }) {
  const allowedBy = new Map(licences.map((l) => [l.licence, l.allowed]));
  const ruled = (rule) => new Set(findings.filter((f) => f.rule === rule && f.extra?.package).map((f) => keyOf(f.extra.package, f.extra.version)));
  const notAllowed = ruled('licence-not-allowed');
  const unknown = ruled('licence-unknown');
  const vulns = new Map();
  for (const f of vulnerabilities || []) {
    if (!f.extra?.package || f.ignored) { continue; }
    const key = keyOf(f.extra.package, f.extra.version);
    const prev = vulns.get(key);
    const worse = !prev || SEVERITIES.indexOf(f.severity) < SEVERITIES.indexOf(prev.severity);
    vulns.set(key, { severity: worse ? f.severity : prev.severity, count: (prev?.count || 0) + 1, ids: [...(prev?.ids || []), f.rule].filter(Boolean) });
  }
  const behind = new Map((outdated || []).map((o) => [keyOf(o.package, o.current), o]));
  return components.map((c) => {
    const name = c.group ? `${c.group}:${c.name}` : c.name;
    const key = keyOf(name, c.version);
    const licence = licenceOf(c);
    const allowed = allowedBy.get(licence || 'Unknown');
    let licenceState = 'ok';
    if (notAllowed.has(key) || allowed === false) {
      licenceState = 'not_allowed';
    } else if (unknown.has(key) || !licence || (allowedBy.has(licence) && allowed !== true)) {
      licenceState = 'unknown';
    }
    const problems = [];
    if (licenceState === 'not_allowed') { problems.push({ kind: 'licence', label: 'Licence not allowed', tone: 'fail' }); }
    const vuln = vulns.get(key);
    if (vuln) {
      problems.push({ kind: 'vulnerable', label: `Vulnerable: ${severityOf(vuln.severity).label}${vuln.count > 1 ? ` · ${vuln.count}` : ''}`, tone: severityOf(vuln.severity).tone, title: vuln.ids.join(', ') });
    }
    if (licenceState === 'unknown') { problems.push({ kind: 'unknown', label: 'Licence unknown', tone: 'warn' }); }
    const old = behind.get(key);
    if (old) { problems.push({ kind: 'outdated', label: old.major ? `Major behind: ${old.latest}` : `Outdated: ${old.latest}`, tone: old.major ? 'warn' : 'info' }); }
    const direct = propOf(c, 'flow:direct');
    return {
      key,
      name,
      version: c.version,
      licence: licence || 'Unknown',
      licenceState,
      services: (propOf(c, 'flow:services') || '').split(',').filter(Boolean),
      dev: c.scope === 'excluded',
      direct: direct === null ? null : direct === 'true',
      problems,
      rank: problems.length ? Math.min(...problems.map((p) => RANK[p.kind])) : OK_RANK,
    };
  });
}

/** "190 OK · 2 licence not allowed · 3 vulnerable · 1 licence unknown · 4 outdated": the rows counted by state. */
export function packageSummary(rows) {
  const count = (kind) => rows.filter((r) => r.problems.some((p) => p.kind === kind)).length;
  const parts = [`${rows.filter((r) => !r.problems.length).length} OK`];
  const add = (n, text) => { if (n) { parts.push(`${n} ${text}`); } };
  add(count('licence'), 'licence not allowed');
  add(count('vulnerable'), 'vulnerable');
  add(count('unknown'), 'licence unknown');
  add(count('outdated'), 'outdated');
  return parts.join(' · ');
}

const LICENCE_TONE = { not_allowed: 'fail', unknown: 'warn' };

function dependencyOf(row) {
  const kind = row.direct === null ? null : row.direct ? 'direct' : 'transitive';
  return [kind, row.dev ? 'dev' : null].filter(Boolean).join(', ') || 'runtime';
}

function columns(onOpenCheck) {
  return [
    {
      key: 'status',
      label: 'Status',
      width: 190,
      wrap: 2,
      value: (r) => r.rank,
      title: (r) => (r.problems.length ? r.problems.map((p) => p.label).join(' · ') : 'OK'),
      render: (r) => (r.problems.length ? (
        <span className="quality-pkg-status">
          {r.problems.map((p) => (p.kind === 'vulnerable' && onOpenCheck ? (
            <button className={`quality-pill is-${p.tone} quality-pill-link`} key={p.kind} onClick={() => onOpenCheck('vulnerabilities')} title={`${p.title ? `${p.title}: ` : ''}open Vulnerabilities`} type="button">{p.label}</button>
          ) : <Pill key={p.kind} title={p.title} tone={p.tone}>{p.label}</Pill>))}
        </span>
      ) : <Pill tone="pass">OK</Pill>),
    },
    { key: 'name', label: 'Package', minWidth: 200, value: (r) => r.name, className: 'quality-mono' },
    { key: 'version', label: 'Version', width: 110, value: (r) => r.version, className: 'quality-mono' },
    {
      key: 'licence',
      label: 'Licence',
      width: 150,
      value: (r) => r.licence,
      render: (r) => (LICENCE_TONE[r.licenceState] ? <Pill tone={LICENCE_TONE[r.licenceState]}>{r.licence}</Pill> : <span className="quality-mono">{r.licence}</span>),
    },
    { key: 'service', label: 'Service', width: 140, wrap: 2, value: (r) => r.services.join(', '), render: (r) => r.services.join(', ') || '–' },
    { key: 'dependency', label: 'Dependency', width: 130, value: dependencyOf },
  ];
}

export function PackagesTable({ projectId, check, licence = '', onLicence, onOpenCheck = null, service = '' }) {
  const [sbom, setSbom] = useState(null);
  const [failure, setFailure] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState(null);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [find, setFind] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const ranAt = check.startedAt || check.ranAt || null;

  const load = useCallback(async () => {
    setFailure(null);
    try {
      setSbom(await getSbom(projectId));
    } catch (e) {
      setFailure(e);
    }
    // Vulnerabilities not run (or an older sandbox): the rows just say nothing about them.
    try {
      const found = await getCheck(projectId, 'vulnerabilities', { limit: 2000 });
      setVulnerabilities(found?.findings || null);
    } catch {
      setVulnerabilities(null);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load, ranAt]);

  const details = check.details || {};
  const rows = useMemo(() => packageRows({
    components: sbom?.components || [], licences: details.licences || [], findings: check.findings || [], vulnerabilities, outdated: details.outdated,
  }), [sbom, details.licences, details.outdated, check.findings, vulnerabilities]);
  const scoped = service ? rows.filter((r) => r.services.includes(service)) : rows;
  const words = find.trim().toLowerCase();
  const shown = scoped.filter((r) => (!problemsOnly || r.problems.length)
    && (!licence || r.licence === licence)
    && (!words || `${r.name} ${r.version} ${r.licence} ${r.services.join(' ')}`.toLowerCase().includes(words)));
  const problems = scoped.filter((r) => r.problems.length).length;
  const licenceNames = [...new Set(scoped.map((r) => r.licence))].sort();

  if (failure && !sbom) {
    return (
      <Banner actions={<button className="quality-btn is-sm" onClick={load} type="button">Try again</button>} testId="quality-packages-error" tone="warn">
        The package list could not be loaded: {errorText(failure)}
      </Banner>
    );
  }
  if (!sbom) { return <p className="quality-muted quality-small" role="status">Loading the packages…</p>; }
  if (!rows.length) { return null; }

  return (
    <div className="quality-packages" data-testid="quality-packages">
      <p className="quality-small" data-testid="quality-packages-summary"><strong>{plural(scoped.length, 'package')}:</strong> {packageSummary(scoped)}</p>
      {!problems && <p className="quality-pkg-ok quality-small" data-testid="quality-packages-ok">{passingMessage('dependencies', { details: { ...details, packages: scoped.length } })}</p>}
      <div aria-label="Filter the packages" className="quality-filters" role="group">
        <label className="quality-sr" htmlFor="quality-pkg-find">Find a package</label>
        <input className="quality-input" id="quality-pkg-find" onChange={(e) => { setFind(e.target.value); setLimit(PAGE); }} placeholder="Package, version, licence, service…" type="search" value={find} />
        <label className="quality-sr" htmlFor="quality-pkg-show">Show</label>
        <select className="quality-input" id="quality-pkg-show" onChange={(e) => { setProblemsOnly(e.target.value === 'problems'); setLimit(PAGE); }} value={problemsOnly ? 'problems' : 'all'}>
          <option value="all">All packages</option>
          <option value="problems">Problems only ({problems})</option>
        </select>
        <label className="quality-sr" htmlFor="quality-pkg-licence">Licence</label>
        <select className="quality-input" id="quality-pkg-licence" onChange={(e) => { onLicence(e.target.value); setLimit(PAGE); }} value={licence}>
          <option value="">Every licence</option>
          {licenceNames.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <div className="quality-table-box">
        <DataTable
          className="quality-table"
          columns={columns(onOpenCheck)}
          defaultSort={{ key: 'status', dir: 'asc' }}
          empty={<span className="quality-muted">No package matches.</span>}
          rowKey="key"
          rowProps={(r) => ({ className: r.problems.length ? `is-problem is-${r.problems[0].tone}` : undefined, 'data-testid': `quality-package-${r.key}` })}
          rows={shown.slice(0, limit)}
          storageKey="flow.quality.packages.hiddenColumns"
          testId="quality-packages-table"
        />
      </div>
      {shown.length > limit && (
        <p className="quality-pad-foot quality-small">
          Showing {limit} of {shown.length}.{' '}
          <button className="quality-btn is-sm" onClick={() => setLimit((n) => n + PAGE)} type="button">Show {Math.min(PAGE, shown.length - limit)} more</button>
        </p>
      )}
    </div>
  );
}

PackagesTable.propTypes = {
  // The Dependencies check's result (its licences, outdated and findings).
  check: PropTypes.object.isRequired,
  // The licence shown ('' every one); the licence chips and the filter set it with `onLicence(licence)`.
  licence: PropTypes.string,
  onLicence: PropTypes.func.isRequired,
  // (checkId) => void: "Vulnerable" opens the Vulnerabilities check.
  onOpenCheck: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  // The page's service ('' every one).
  service: PropTypes.string,
};

/**
 * One line per service that has or needs a lockfile: its lockfile(s) and how many packages they list, "No lockfile"
 * with Create lockfile, or that it declares no dependencies. From the check (`lockfiles`, `noLockfile`) and what Quality
 * detected per service (`lockfileMissing`, `packageManager`).
 */
export function lockfilesByService(details = {}, detected = []) {
  const files = details.lockfiles || [];
  const missing = new Set(details.noLockfile || []);
  const names = [...new Set([
    ...detected.filter((s) => s.packageManager || (s.lockfiles || []).length || (s.lockfileMissing || []).length).map((s) => s.service),
    ...files.map((l) => l.service),
    ...missing,
  ])];
  return names.map((service) => {
    const own = files.filter((l) => l.service === service);
    const svc = detected.find((s) => s.service === service);
    const needs = missing.has(service) || (svc?.lockfileMissing || []).length > 0;
    if (own.length && !needs) { return { service, state: 'ok', files: own }; }
    if (needs) { return { service, state: 'missing', files: own, manifests: (svc?.lockfileMissing || []).map((m) => m.manifest) }; }
    return { service, state: 'none', files: [] };
  });
}

export function LockfilesByService({ details, detected = [], canEdit = false, onCreateLockfiles = null, lockfileBusy = false }) {
  const rows = lockfilesByService(details, detected);
  if (!rows.length) { return null; }
  return (
    <ul aria-label="Lockfiles per service" className="quality-notes" data-testid="quality-lockfiles-by-service">
      {rows.map((r) => (
        <li data-testid={`quality-lockfile-of-${r.service}`} key={r.service}>
          <span className="quality-mono">{r.service}</span>
          {r.state === 'ok' && <Pill tone="pass">Lockfile</Pill>}
          {r.state === 'missing' && <Pill tone="warn">No lockfile</Pill>}
          {r.state === 'none' && <Pill tone="info">Nothing to lock</Pill>}
          <span className="quality-grow">
            {r.files.map((l) => `${l.file.split('/').pop()}${l.error ? ` (${l.error})` : ` · ${plural(l.packages, 'package')}`}`).join(', ')}
            {r.state === 'missing' && `${r.files.length ? ' · ' : ''}${r.manifests?.length ? `${r.manifests.join(', ')} is not locked` : 'its dependencies are not locked'}: they can't be checked`}
            {r.state === 'none' && 'declares no dependencies'}
          </span>
          {r.state === 'missing' && canEdit && onCreateLockfiles && (
            <button className="quality-btn is-sm is-primary" disabled={lockfileBusy} onClick={() => onCreateLockfiles([r.service])} type="button">{lockfileBusy ? 'Creating…' : 'Create lockfile'}</button>
          )}
        </li>
      ))}
    </ul>
  );
}

LockfilesByService.propTypes = {
  canEdit: PropTypes.bool,
  details: PropTypes.object.isRequired,
  // The overview's detected services (`lockfileMissing`, `packageManager`, `lockfiles`).
  detected: PropTypes.arrayOf(PropTypes.object),
  lockfileBusy: PropTypes.bool,
  // (services) => void: "Create lockfiles" for that one service.
  onCreateLockfiles: PropTypes.func,
};
